// Pure state logic for the role editor. `selected` is a Set of permission keys;
// every function returns a new Set and never mutates its input.

export function areaViewKey(area) {
  const view = area.permissions.find((p) => p.key === `${area.key}.view`);
  return view ? view.key : null;
}

export function grantedCount(area, selected) {
  return area.permissions.filter((p) => selected.has(p.key)).length;
}

export function areaMode(area, selected) {
  const granted = grantedCount(area, selected);
  if (granted === 0) return 'none';
  if (granted === area.permissions.length) return 'full';
  const view = areaViewKey(area);
  if (view && granted === 1 && selected.has(view)) return 'view';
  return 'custom';
}

export function applyMode(area, mode, selected) {
  const next = new Set(selected);
  for (const p of area.permissions) next.delete(p.key);
  if (mode === 'full') area.permissions.forEach((p) => next.add(p.key));
  if (mode === 'view') {
    const view = areaViewKey(area);
    if (view) next.add(view);
  }
  return next;
}

export function dependents(area, key, selected) {
  return area.permissions.filter((p) => selected.has(p.key) && p.implies.includes(key)).length;
}

export function toggleKey(area, key, selected) {
  const next = new Set(selected);
  if (next.has(key)) {
    if (dependents(area, key, selected) > 0) return next; // still required
    next.delete(key);
    return next;
  }
  next.add(key);
  const perm = area.permissions.find((p) => p.key === key);
  (perm ? perm.implies : []).forEach((k) => next.add(k));
  return next;
}

// Catalog-wide variants: a key can be implied by a selected permission in ANY
// area (e.g. `sync.user` implies `users.view`), so locking a checkbox must
// look across the whole catalog, not just the area currently being edited.

export function dependentsIn(allPermissions, key, selected) {
  return allPermissions.filter((p) => selected.has(p.key) && p.implies.includes(key)).length;
}

export function toggleKeyIn(area, key, selected, allPermissions) {
  const next = new Set(selected);
  if (next.has(key)) {
    if (dependentsIn(allPermissions, key, selected) > 0) return next; // still required, anywhere in the catalog
    next.delete(key);
    return next;
  }
  next.add(key);
  const perm = area.permissions.find((p) => p.key === key);
  (perm ? perm.implies : []).forEach((k) => next.add(k));
  return next;
}

export function applyModeIn(area, mode, selected, allPermissions) {
  const next = new Set(selected);
  for (const p of area.permissions) {
    // A key still implied by a selected permission in another area must not be removed.
    if (dependentsIn(allPermissions, p.key, next) > 0) continue;
    next.delete(p.key);
  }
  if (mode === 'full') area.permissions.forEach((p) => next.add(p.key));
  if (mode === 'view') {
    const view = areaViewKey(area);
    if (view) next.add(view);
  }
  return next;
}

export function withImplied(selected, allPermissions) {
  const next = new Set(selected);
  const byKey = new Map(allPermissions.map((p) => [p.key, p]));
  for (const key of selected) {
    const perm = byKey.get(key);
    (perm ? perm.implies : []).forEach((k) => next.add(k));
  }
  return next;
}

export function matchesQuery(permission, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  return `${permission.label} ${permission.description}`.toLowerCase().includes(q);
}

export function summarize(catalog, selected) {
  const byArea = new Map(catalog.areas.map((a) => [a.key, a]));
  const destinations = catalog.destinations
    .filter((d) => d.requires.some((k) => selected.has(k)))
    .map((d) => {
      const area = byArea.get(d.area);
      const held = area ? area.permissions.filter((p) => selected.has(p.key)) : [];
      const viewOnly = held.length > 0 && held.every((p) => p.key.endsWith('.view'));
      return viewOnly && areaViewKey(area) ? `${d.label} (view only)` : d.label;
    });
  const total = catalog.areas.reduce((n, a) => n + a.permissions.length, 0);
  return { destinations, count: selected.size, total };
}

export function sameSet(a, b) {
  if (a.size !== b.size) return false;
  for (const k of a) if (!b.has(k)) return false;
  return true;
}
